import React, { useState, useEffect } from "react";
import {
  LanguageIcon,
  ChevronRightIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { updateProfile, getUserProfile } from "@/store/slices/userSlice";
import { getLoginUserData } from "@/store/slices/newDeliverySlice";
import ShowLoader from "@/components/Loader";
import { setAppLanguage } from "@/utils/language";

export default function DeliveryLanguageSubMenu() {
  const { t, i18n } = useTranslation("delivery");
  const dispatch = useDispatch();
  const { user, loading } = useSelector((state) => state.user);
  const [isOpen, setIsOpen] = useState(false);

  const [selectedLang, setSelectedLang] = useState(
    user?.language || localStorage.getItem("app_language") || i18n.language || "en"
  );

  useEffect(() => {
    if (user?.language) {
      setSelectedLang(user.language);
      i18n.changeLanguage(user.language);
    }
  }, [user]);

  const changeLanguage = async (lang) => {
    if (selectedLang === lang) return;

    try {
      setSelectedLang(lang);
      setAppLanguage(lang);
      
      // Global i18next instance update
      await i18n.changeLanguage(lang);

      const payload = {
        id: user?.id,
        first_name: user?.first_name,
        email: user?.email,
        role: user?.roles?.[0] || user?.role,
        language: lang,
      };

      const res = await dispatch(updateProfile(payload)).unwrap();

      if (res?.status) {
        await Promise.all([
          dispatch(getUserProfile()),
          dispatch(getLoginUserData())
        ]);
        setIsOpen(false);
      }
    } catch (error) {
      console.error("Delivery Language update error:", error);
      if (user?.language) setSelectedLang(user.language);
    }
  };

  const isArabic = selectedLang === "ar" || selectedLang?.startsWith("ar");

  return (
    <div className="w-full">
      {loading && <ShowLoader />}
      
      <div
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex items-center justify-between cursor-pointer group py-1"
      >
        <div className="flex items-center gap-3">
          <LanguageIcon className="h-5 w-5 text-gray-800" />
          <span className="text-gray-900 font-medium group-hover:text-amber-500 transition">
            {t("languages")}
          </span>
        </div>

        <ChevronRightIcon
          className={`h-5 w-5 text-gray-800 transition-transform duration-200 ${
            isOpen ? "rotate-90" : ""
          }`}
        />
      </div>

      {isOpen && (
        <div className="mt-3 ml-8 space-y-2">
          <button
            type="button"
            onClick={() => changeLanguage("en")}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-100 transition"
          >
            <span className="text-sm font-medium text-gray-800">🇬🇧 English</span>
            {!isArabic && (
              <CheckIcon className="h-4 w-4 text-green-600 stroke-[3]" />
            )}
          </button>

          <button
            type="button"
            onClick={() => changeLanguage("ar")}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-100 transition"
          >
            <span className="text-sm font-medium text-gray-800">🇸🇦 العربية</span>
            {isArabic && (
              <CheckIcon className="h-4 w-4 text-green-600 stroke-[3]" />
            )}
          </button>
        </div>
      )}
    </div>
  );
}